import { Alert, Link, Text } from 'evergreen-ui';
import React, { FC } from 'react';

const NEW_PORTAL_URL = 'https://portal.getgarner.com';

/**
 * The app disables document scrolling (`body { position: fixed; overflow: hidden }` in app.css), so
 * views size themselves against the viewport with `calc(100vh - ...)`. Publishing the banner's
 * height lets those calculations account for the space it takes up. The variable defaults to 0px,
 * so deleting this banner after the migration restores the original sizing with no other changes.
 */
export const BANNER_HEIGHT_VAR = '--deprecation-banner-height';

const useMeasuredHeight = (element: HTMLElement | null) => {
  React.useEffect(() => {
    if (!element) return;

    const root = document.documentElement;
    const observer = new ResizeObserver(([entry]) => {
      root.style.setProperty(BANNER_HEIGHT_VAR, `${entry.contentRect.height}px`);
    });
    observer.observe(element);

    return () => {
      observer.disconnect();
      root.style.removeProperty(BANNER_HEIGHT_VAR);
    };
  }, [element]);
};

export const DeprecationBanner: FC = () => {
  const [element, setElement] = React.useState<HTMLElement | null>(null);
  useMeasuredHeight(element);

  return (
    <div ref={setElement}>
      <Alert
        intent="warning"
        title="Starting September 4, 2026, eligibility data will be hosted in the new Garner Admin Portal"
        borderRadius={0}
        paddingY={12}
      >
        <Text size={400}>
          Your Client Success Manager will reach out with more details on this transition and to provide access. To
          ensure continuity, changes made here will be reflected in the{' '}
          <Link href={NEW_PORTAL_URL} target="_blank" rel="noopener noreferrer" size={400}>
            new Garner Admin Portal
          </Link>
          .
        </Text>
      </Alert>
    </div>
  );
};
